import React, {
    createContext,
    FunctionComponent,
    KeyboardEvent,
    ReactNode,
    useCallback,
    useRef,
    useState,
} from "react";

import { TabIndex } from "@multiweb/types/react";
import { SharedKey } from "Global/Base/Utils/EventUtils";
import { NavigableComponentProps, NavigationData, PositionSelectionMode } from "../types";
import { getRovingTabIndex } from "../utils";

export type KeyboardNavigationContextType = {
    /** Přidá prvek do navigačních dat. */
    readonly addItem: (itemId: string) => void;
    /** Odstraní prvek z navigačních dat. */
    readonly removeItem: (itemId: string) => void;
    /** Nastaví prvku stav možnosti zaměření. */
    readonly setItemFocusability: (itemId: string, focusable: boolean) => void;
    /** Zaměří prvek podle jeho Id. V případě null nezaměří žádný. */
    readonly focusItem: (itemId: string | null) => void;
    /** Zaměří další prvek podle stisknuté klávesy. */
    readonly focusNextItem: (keyboardEvent: KeyboardEvent) => void;
    /** Zaměří první zaměřitelný prvek. */
    readonly setFirstItemFocusable: () => void;
    /** Vrací stav zaměřitelnosti daného prvku. */
    readonly getNavigationData: (itemId: string) => NavigationData;
};

/**
 * Kontext obsahující callbacky pro navigaci mezi registrovanými prvky a práci s jejich zaměřením.
 */
export const KeyboardNavigationContext = createContext<KeyboardNavigationContextType | null>(null);

export type KeyboardNavigationContextProviderProps = {
    /** Klávesa pro označení následujícího prvku */
    nextItemKey?: SharedKey;
    /** Klávesa pro označení předchozího prvku */
    previousItemKey?: SharedKey;
    /** Kontext reaguje na stisk nextItemKey a previousItemKey exkluzivně a zabraňuje propagaci událostí */
    isExclusive?: boolean;
    /** Režim algoritmu pro výběr pozice zaměřeného prvku */
    positionSelectionMode?: PositionSelectionMode;
    children: ReactNode;
};
export const KeyboardNavigationContextProvider: FunctionComponent<KeyboardNavigationContextProviderProps> = ({
    nextItemKey = SharedKey.ArrowRight,
    previousItemKey = SharedKey.ArrowLeft,
    isExclusive = true,
    positionSelectionMode = PositionSelectionMode.default,
    children,
}) => {
    /** Navigační data všech prvků v kontextu */
    const navigationDataRef = useRef(new Array<NavigableComponentProps>());
    /** Id aktuálně zaměřitelného prvku */
    const focusableItemIdRef = useRef("");
    /** Příznak, zda je nějaký prvek reálně zaměřený */
    const isAnyItemFocused = useRef(false);
    /** Aktuálně zaměřený prvek */
    const [focusedItemIdState, setFocusedItemIdState] = useState<string | null>(null);
    /** Aktuálně zaměřitelný prvek */
    const [focusableItemIdState, setFocusableItemIdState] = useState<string | null>(null);

    /** Pomocná funkce pro nastavení zaměřitelnosti navigačního prvku */
    const setItemFocusable = (itemId: string) => {
        setFocusableItemIdState(itemId);
        focusableItemIdRef.current = itemId;
    };

    /** Pomocná funkce pro zaměření navigačního prvku */
    const setItemFocused = (itemId: string) => {
        setItemFocusable(itemId);
        setFocusedItemIdState(itemId);
        isAnyItemFocused.current = true;
    };

    /** Pomocná funkce pro vynulování vnitřních stavů */
    const setNoneItemFocusable = () => {
        setFocusableItemIdState(null);
        setFocusedItemIdState(null);
        focusableItemIdRef.current = "";
        isAnyItemFocused.current = false;
    };

    /** Nastaví zaměřitelný první navigační prvek. */
    const setFirstItemFocusable = useCallback(() => {
        const focusableItems = navigationDataRef.current.filter(item => item.focusable);
        if (focusableItems.length > 0) {
            setItemFocusable(focusableItems[0].id);
            setFocusedItemIdState(null);
        } else {
            // Žádný navigační prvek není zaměřitelný, vynulujeme vnitřní stavy.
            setNoneItemFocusable();
        }
    }, []);

    /** Nastaví zaměřitelný poslední navigační prvek. */
    const setLastItemFocusable = useCallback(() => {
        const focusableItems = navigationDataRef.current.filter(item => item.focusable);
        if (focusableItems.length > 0) {
            setItemFocusable(focusableItems[focusableItems.length - 1].id);
            setFocusedItemIdState(null);
        } else {
            // Žádný navigační prvek není zaměřitelný, vynulujeme vnitřní stavy.
            setNoneItemFocusable();
        }
    }, []);

    /** Nastaví prvku stav možnosti zaměření. */
    const setItemFocusability = useCallback((itemId: string, focusable: boolean) => {
        const navigationDataArray = navigationDataRef.current;
        const focusableItems = navigationDataArray.filter(item => item.focusable);
        const currentItemIndex = focusableItems.findIndex(item => focusableItemIdRef.current === item.id);
        const foundItem = navigationDataArray.find(item => item.id === itemId);
        const foundItemIndex = focusableItems.findIndex(item => item.id === itemId);
        if (foundItem) {
            foundItem.focusable = focusable;
            const updatedFocusableItems = navigationDataArray.filter(item => item.focusable);
            if (currentItemIndex >= 0 && foundItemIndex <= currentItemIndex) {
                // Došlo ke změně zaměřitelnosti před aktuálně zaměřeným prvkem, takže došlo ke změně jeho pozice.
                // Je potřeba případně aktualizovat aktuálně zaměřený prvek podle nastavení positionSelectionMode.
                let newFocusableIndex = currentItemIndex;
                if (positionSelectionMode === PositionSelectionMode.preferFirst) {
                    newFocusableIndex = 0;
                }
                switch (positionSelectionMode) {
                    case PositionSelectionMode.holdPosition:
                    case PositionSelectionMode.preferFirst:
                        if (updatedFocusableItems.length > newFocusableIndex) {
                            setItemFocusable(updatedFocusableItems[newFocusableIndex].id);
                            if (isAnyItemFocused.current) {
                                setFocusedItemIdState(updatedFocusableItems[newFocusableIndex].id);
                            }
                        } else if (updatedFocusableItems.length > 0) {
                            setLastItemFocusable();
                        } else {
                            // Žádný navigační prvek není zaměřitelný, vynulujeme vnitřní stavy.
                            setNoneItemFocusable();
                        }
                }
            }
            if (foundItem.id === focusableItemIdRef.current && !foundItem.focusable) {
                // Pokud byl právě tento prvek aktuálně zaměřitelný a byla mu odebrána zaměřitelnost,
                // zachováme se podle nastavení positionSelectionMode.
                switch (positionSelectionMode) {
                    case PositionSelectionMode.preferFirst:
                        setFirstItemFocusable();
                        break;
                    case PositionSelectionMode.default:
                        if (updatedFocusableItems.length > currentItemIndex) {
                            setItemFocusable(updatedFocusableItems[currentItemIndex].id);
                            if (isAnyItemFocused.current) {
                                setFocusedItemIdState(updatedFocusableItems[currentItemIndex].id);
                            }
                        } else if (updatedFocusableItems.length > 0) {
                            setLastItemFocusable();
                        }
                }
            }
        }
        if (!focusableItemIdRef.current) {
            // Pokud ještě žádný prvek není zaměřitelný, nastavíme zaměřitelný první prvek.
            setFirstItemFocusable();
        }
    }, [positionSelectionMode, setFirstItemFocusable, setLastItemFocusable]);

    /** Přidá prvek do navigačních dat. */
    const addItem = useCallback((itemId: string) => {
        const navigationDataArray = navigationDataRef.current;
        const foundItemIndex = navigationDataArray.findIndex(item => item.id === itemId);
        const navigationDataItem = { id: itemId, focusable: false };
        if (foundItemIndex >= 0) {
            navigationDataArray[foundItemIndex] = navigationDataItem;
        } else {
            navigationDataArray.push(navigationDataItem);
        }
    }, []);

    /** Odstraní prvek z navigačních dat. */
    const removeItem = useCallback((itemId: string) => {
        // Prvek ve skutečnosti neodstraníme, jenom mu nastavíme focusable na false,
        // abychom neztratili jeho pozici, např. při překreslení komponenty.
        setItemFocusability(itemId, false);
    }, [setItemFocusability]);

    /** Zaměří prvek podle jeho Id. */
    const focusItem = (itemId: string | null) => {
        if (itemId) {
            const item = navigationDataRef.current.find(currentItem => currentItem.id === itemId);
            if (item) {
                setItemFocused(itemId);
            }
        } else {
            setFocusedItemIdState(null);
            isAnyItemFocused.current = false;
        }
    };

    /** Zaměří další prvek podle stisknuté klávesy. */
    const focusNextItem = (keyboardEvent: KeyboardEvent) => {
        if ([nextItemKey, previousItemKey].includes(keyboardEvent.key as SharedKey)) {
            const focusableItems = navigationDataRef.current.filter(item => item.focusable);
            const currentItem = focusableItems.find(item => focusableItemIdState === item.id);
            if (currentItem) {
                const focusedItemIndex = focusableItems.indexOf(currentItem);
                const nextFocusedItem = focusableItems[(
                    focusableItems.length + focusedItemIndex + (keyboardEvent.key === nextItemKey ? 1 : -1)
                ) % focusableItems.length];
                setItemFocused(nextFocusedItem.id);
                if (isExclusive) {
                    // Pokud je kontext exkluzivní, zabráníme propagaci události do dalších kontextů.
                    keyboardEvent.stopPropagation();
                }
            }
        }
    };

    /** Vrací stav zaměřitelnosti daného prvku a jeho tabIndex. */
    const getNavigationData = (itemId: string): {isFocused: boolean; tabIndex: TabIndex} => {
        return {
            isFocused: focusedItemIdState === itemId,
            tabIndex: getRovingTabIndex(focusableItemIdState === itemId),
        };
    };

    return <KeyboardNavigationContext.Provider
        value={{
            addItem,
            removeItem,
            setItemFocusability,
            focusItem,
            focusNextItem,
            setFirstItemFocusable,
            getNavigationData,
        }}
    >
        {children}
    </KeyboardNavigationContext.Provider>;
};
